/** Task status values used by API and Tasks UI. */
export type TaskStatus = "new" | "in_process" | "document_check" | "done" | "rejected";

export const TASK_STATUS_IDS: readonly TaskStatus[] = [
  "new",
  "in_process",
  "document_check",
  "done",
  "rejected",
] as const;

const TASK_STATUS_SET = new Set<string>(TASK_STATUS_IDS);

const normalizeStatus = (value: unknown): string => (value == null ? "" : String(value).trim());

const isTaskStatus = (value: string): value is TaskStatus => TASK_STATUS_SET.has(value);

/** Maps UI status id → API status (1:1 for current API). */
export const mapTaskUiStatusToApi = (uiStatus: string): TaskStatus | null => {
  const normalized = normalizeStatus(uiStatus);
  if (isTaskStatus(normalized)) return normalized;

  // Legacy API values that may still appear in old records
  switch (normalized) {
    case "completed":
      return "done";
    case "approved":
      return "in_process";
    default:
      return null;
  }
};

/** Maps API status → UI status id for table/popup display. */
export const mapTaskApiStatusToUi = (
  apiStatus: string,
  context?: { attached_file?: string | null },
): TaskStatus => {
  const normalized = normalizeStatus(apiStatus);
  if (isTaskStatus(normalized)) return normalized;

  switch (normalized) {
    case "completed":
      return "done";
    case "approved":
      return context?.attached_file?.trim() ? "document_check" : "in_process";
    default:
      return "new";
  }
};

/** Maps UI filter/tab status ids to API values for `/tasks/filters/apply`. */
export const mapTaskUiStatusFilterToApi = (uiStatus: string): string => mapTaskUiStatusToApi(uiStatus) ?? uiStatus;
